/**
 * CogoToolRegistry — central registry for WebMCP tools.
 *
 * Each tool has a name, description, input/output JSON Schema,
 * and an async handler. The registry supports listener notification
 * on changes, manifest export, and thread-safe tool invocation.
 */

// ── Types ──────────────────────────────────────────────────

export interface JsonSchema {
  type: string
  properties?: Record<string, JsonSchema>
  required?: string[]
  items?: JsonSchema
  enum?: unknown[]
  description?: string
  default?: unknown
  [key: string]: unknown
}

export interface WebMCPToolMetadata {
  /** Origin block that exposed this tool */
  blockName?: string
  /** Whether the tool only reads state (no mutations) */
  readOnly?: boolean
  /** Arbitrary tags for filtering */
  tags?: string[]
}

export interface WebMCPTool {
  name: string
  description: string
  inputSchema: JsonSchema
  outputSchema?: JsonSchema
  handler: (params: Record<string, unknown>) => unknown | Promise<unknown>
  metadata?: WebMCPToolMetadata
}

export interface ToolManifestEntry {
  name: string
  description: string
  inputSchema: JsonSchema
  outputSchema?: JsonSchema
  metadata?: WebMCPToolMetadata
}

export type ToolsChangedListener = (tools: ToolManifestEntry[]) => void

// ── Registry ───────────────────────────────────────────────

export class CogoToolRegistry {
  private tools = new Map<string, WebMCPTool>()
  private listeners = new Set<ToolsChangedListener>()

  /** Register a tool. Overwrites any existing tool with the same name. */
  register(tool: WebMCPTool): void {
    this.tools.set(tool.name, tool)
    this.notifyListeners()
  }

  /** Remove a tool by name. Returns true if it existed. */
  unregister(name: string): boolean {
    const existed = this.tools.delete(name)
    if (existed) this.notifyListeners()
    return existed
  }

  /** Get a tool by name, or undefined if not registered. */
  getTool(name: string): WebMCPTool | undefined {
    return this.tools.get(name)
  }

  /** Get all registered tools. */
  getTools(): WebMCPTool[] {
    return Array.from(this.tools.values())
  }

  /**
   * Invoke a tool by name with the given params.
   * Throws if the tool is not registered.
   */
  async callTool(name: string, params: Record<string, unknown>): Promise<unknown> {
    const tool = this.tools.get(name)
    if (!tool) {
      throw new Error(`WebMCP tool not found: "${name}"`)
    }
    return tool.handler(params)
  }

  /** Get W3C-compatible manifest (tool definitions without handlers). */
  getManifest(): ToolManifestEntry[] {
    return Array.from(this.tools.values()).map((t) => ({
      name: t.name,
      description: t.description,
      inputSchema: t.inputSchema,
      outputSchema: t.outputSchema,
      metadata: t.metadata,
    }))
  }

  /** Subscribe to tools-changed events. Returns an unsubscribe function. */
  onToolsChanged(listener: ToolsChangedListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /** Remove all tools and notify listeners. */
  clear(): void {
    if (this.tools.size === 0) return
    this.tools.clear()
    this.notifyListeners()
  }

  /** Number of registered tools. */
  get size(): number {
    return this.tools.size
  }

  private notifyListeners(): void {
    const manifest = this.getManifest()
    for (const listener of this.listeners) {
      try {
        listener(manifest)
      } catch {
        // Listener errors should not break the registry
      }
    }
  }
}
